import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { Bell, Check, CheckCheck, Trash2, Package, Truck, Info, Calendar, TrendingUp } from "lucide-react";
import UserSidebar from "@/components/UserSidebar";
import { fetchNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification, Notification } from "@/services/customerService";
import { toast } from "sonner";
import moment from "moment";
import "moment/locale/ja";

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    moment.locale('ja');
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await fetchNotifications();
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error("通知の取得に失敗しました", error);
      toast.error("通知の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await markNotificationAsRead(id);
      setNotifications(notifications.map(notif =>
        notif.id === id ? { ...notif, read_at: new Date().toISOString() } : notif
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
      toast.success("既読にしました");
    } catch (error) {
      console.error("既読処理に失敗しました", error);
      toast.error("既読処理に失敗しました");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      const now = new Date().toISOString();
      setNotifications(notifications.map(notif => ({ ...notif, read_at: now })));
      setUnreadCount(0);
      toast.success("すべて既読にしました");
    } catch (error) {
      console.error("一括既読処理に失敗しました", error);
      toast.error("一括既読処理に失敗しました");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("この通知を削除しますか？")) return;

    try {
      await deleteNotification(id);
      const notification = notifications.find(n => n.id === id);
      setNotifications(notifications.filter(notif => notif.id !== id));
      if (notification && !notification.read_at) {
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
      toast.success("通知を削除しました");
    } catch (error) {
      console.error("削除に失敗しました", error);
      toast.error("削除に失敗しました");
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === "all") return true;
    if (filter === "unread") return !notif.read_at;
    if (filter === "read") return notif.read_at;
    return true;
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "order_shipped":
        return <Package className="w-5 h-5 text-blue-500" />;
      case "order_delivered":
        return <Truck className="w-5 h-5 text-green-500" />;
      case "event_started":
        return <Calendar className="w-5 h-5 text-purple-500" />;
      case "event_ending_soon":
        return <TrendingUp className="w-5 h-5 text-orange-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const unreadNotifs = notifications.filter(n => !n.read_at);
  const readNotifs = notifications.filter(n => n.read_at);

  return (
    <>
      <Helmet>
        <title>通知</title>
        <meta property="og:title" content="通知" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="max-w-screen-lg mx-auto px-4 pb-4 sm:pb-8 pt-20">
          <div className="flex gap-8">
            {/* サイドバー - デスクトップのみ */}
            <div className="hidden lg:block">
              <UserSidebar />
            </div>

            {/* メインコンテンツ */}
            <div className="flex-1">
              {/* タイトルセクション */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-400 border-theme rounded-theme flex items-center justify-center shadow-theme-sm">
                    <Bell className="h-5 w-5 text-black" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl [font-weight:var(--theme-font-weight)] text-black">通知</h1>
                      {unreadCount > 0 && (
                        <span className="px-2 py-0.5 bg-red-400 border-theme text-black text-xs [font-weight:var(--theme-font-weight)] rounded-theme">
                          {unreadCount}
                        </span>
                      )}
                    </div>
                    <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">お知らせや更新情報をお届けします</p>
                  </div>
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-400 border-theme rounded-theme [font-weight:var(--theme-font-weight-button)] text-black shadow-theme-sm hover:shadow-theme-hover hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
                  >
                    <CheckCheck className="w-4 h-4" />
                    すべて既読
                  </button>
                )}
              </div>

              {/* フィルター */}
              <div className="mb-6 flex gap-2 flex-wrap">
                <button
                  onClick={() => setFilter("all")}
                  className={`px-4 py-2 rounded-theme text-sm [font-weight:var(--theme-font-weight-button)] border-theme transition-all ${
                    filter === "all"
                      ? "bg-theme-primary text-theme-button shadow-theme-sm"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  全て ({notifications.length})
                </button>
                <button
                  onClick={() => setFilter("unread")}
                  className={`px-4 py-2 rounded-theme text-sm [font-weight:var(--theme-font-weight-button)] border-theme transition-all ${
                    filter === "unread"
                      ? "bg-theme-primary text-theme-button shadow-theme-sm"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  未読 ({unreadNotifs.length})
                </button>
                <button
                  onClick={() => setFilter("read")}
                  className={`px-4 py-2 rounded-theme text-sm [font-weight:var(--theme-font-weight-button)] border-theme transition-all ${
                    filter === "read"
                      ? "bg-theme-primary text-theme-button shadow-theme-sm"
                      : "bg-white text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  既読 ({readNotifs.length})
                </button>
              </div>

              {/* コンテンツ */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-theme-primary"></div>
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="bg-white border-theme rounded-theme-lg shadow-theme p-12 text-center">
                  <div className="w-16 h-16 bg-gray-200 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4">
                    <Bell className="h-8 w-8 text-gray-500" />
                  </div>
                  <h3 className="text-lg [font-weight:var(--theme-font-weight)] text-black mb-2">
                    通知はありません
                  </h3>
                  <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">
                    {filter === "unread" && "未読の通知はありません"}
                    {filter === "read" && "既読の通知はありません"}
                    {filter === "all" && "通知が届くとここに表示されます"}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`bg-white rounded-theme-lg shadow-theme p-4 transition ${
                        !notification.read_at
                          ? "border-[length:var(--theme-border-width)] border-blue-400"
                          : "border-theme"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* アイコン */}
                        <div className="flex-shrink-0 mt-1">
                          <div className="w-10 h-10 bg-gray-100 border-theme rounded-theme flex items-center justify-center">
                            {getNotificationIcon(notification.notification_type)}
                          </div>
                        </div>

                        {/* コンテンツ */}
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-base [font-weight:var(--theme-font-weight)] text-black mb-1">
                                {notification.title}
                              </h3>
                              <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600">
                                {notification.message}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {!notification.read_at && (
                                <button
                                  onClick={() => handleMarkAsRead(notification.id)}
                                  className="p-2 bg-blue-100 border-theme rounded-theme hover:bg-blue-200 transition"
                                  aria-label="既読にする"
                                  title="既読にする"
                                >
                                  <Check className="w-4 h-4 text-black" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(notification.id)}
                                className="p-2 bg-red-100 border-theme rounded-theme hover:bg-red-200 transition"
                                aria-label="削除"
                                title="削除"
                              >
                                <Trash2 className="w-4 h-4 text-black" />
                              </button>
                            </div>
                          </div>

                          {/* 時刻とアクション */}
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                              {moment(notification.created_at).fromNow()}
                            </span>
                            {notification.action_url && (
                              <a
                                href={notification.action_url}
                                className="text-sm [font-weight:var(--theme-font-weight)] text-theme-primary hover:opacity-80"
                              >
                                詳細を見る →
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationsPage;
